import { useState, useEffect, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { useUserGeneratedContent } from '../../../models/user-generated-contents/context';
import { usePushToastOrFlashMessage } from '../../../components/v2/toaster/deprecation-helper';
import useScreenTitle from '../../../common/use-screen-title';

type UserGeneratedContentFormProps = {
  children: (props: { onSave: () => void; onSubmit: () => void }) => ReactNode;
};

const UserGeneratedContentForm = ({
  children,
}: UserGeneratedContentFormProps) => {
  const [navigateAway, setNavigateAway] = useState(false);
  const {
    userGeneratedContent,
    upsertUserGeneratedContent,
    submitDraft,
    hasRequiredInput,
  } = useUserGeneratedContent();
  useScreenTitle(userGeneratedContent.title || 'New Post');
  const { pushMessage } = usePushToastOrFlashMessage();
  const { t } = useTranslation();
  const history = useHistory();

  const handleSubmitSuccess = (messageType: 'created' | 'updated') => {
    setNavigateAway(true);

    pushMessage({
      text: t(`user_generated_content.${messageType}`),
      type: 'success',
    });
  };

  const handleSubmitFailure = (err: Error) => {
    console.error('UGC draft submission error', err);

    pushMessage({
      text: t('errors.default'),
      type: 'error',
    });
  };

  const handleSave = () => {
    upsertUserGeneratedContent({
      onSuccess: () =>
        handleSubmitSuccess(userGeneratedContent.id ? 'updated' : 'created'),
      onError: handleSubmitFailure,
    });
  };

  const handleSubmit = () => {
    submitDraft({
      onSuccess: () =>
        handleSubmitSuccess(userGeneratedContent.id ? 'updated' : 'created'),
      onError: handleSubmitFailure,
    });
  };

  const handleDiscardChanges = () => {
    //TODO: (FE-26994) If there are unsaved changes to the form, there should be a warning to the user (will be addressed in a separate ticket)
    return true;
  };

  useEffect(() => {
    if (navigateAway) history.goBack();
  }, [navigateAway, history]);

  return (
    <>
      <Prompt
        message={handleDiscardChanges}
        when={hasRequiredInput && !navigateAway}
      />

      <form
        aria-label={t('user_generated_content.form_label')}
        onSubmit={(e) => e.preventDefault()}
      >
        {children({ onSave: handleSave, onSubmit: handleSubmit })}
      </form>
    </>
  );
};

export default UserGeneratedContentForm;
